import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useEffect, useState } from "react";
import { Layout } from "@/components/site/Layout";
import { useServicesList } from "@/lib/services";
import { ArrowRight, Mail, MapPin, Phone, ShieldCheck, TreePine, Users, Cake, GraduationCap, Calendar, PartyPopper } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import heroBgImg from "@/assets/bosque-hero.jpg";
import familiesImg from "@/assets/bosque-families.jpg";
import ziplineImg from "@/assets/bosque-zipline.jpg";
import archeryImg from "@/assets/bosque-archery.jpg";
import climbingImg from "@/assets/bosque-climbing.jpg";
import nightImg from "@/assets/bosque-night.jpg";
import introZiplineImg from "@/assets/intro-zipline.jpg";
import introFamilyImg from "@/assets/intro-family.jpg";
import introForestImg from "@/assets/intro-forest.jpg";

export const Route = createFileRoute("/")({
  component: Home,
  head: () => ({
    meta: [
      { title: "Bosque Suspendido — Parque de aventura en los árboles (Sevilla)" },
      {
        name: "description",
        content:
          "Primer parque de aventura en los árboles de Andalucía. Tirolinas, circuitos, paintball, lucha de arqueros y mucho más en plena naturaleza, a 15 minutos de Sevilla.",
      },
      { property: "og:title", content: "Bosque Suspendido — Aventura en los árboles" },
      {
        property: "og:description",
        content:
          "Naturaleza y diversión para toda la familia. Reserva tu aventura en el Centro de Naturaleza La Juliana, Sevilla.",
      },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/" },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
});

function CountUp({ end, suffix = "", duration = 1800 }: { end: number; suffix?: string; duration?: number }) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now: number) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(end * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{value.toLocaleString()}{suffix}</span>;
}

const FALLBACK_IMAGES: Record<string, string> = {
  "circuitos": familiesImg,
  "tirolinas": ziplineImg,
  "lucha-arqueros": archeryImg,
  "escalada": climbingImg,
  "bosque-nocturno": nightImg,
  "paintball": archeryImg,
};

function Home() {
  const { tk } = useT();
  const activities = useServicesList();
  const heroBg = useMedia("home.hero.bg", heroBgImg, "Bosque Suspendido — vuelo en tirolina entre pinos");

  const featured = activities.filter((a) => a.imageUrl || FALLBACK_IMAGES[a.slug]).slice(0, 9);

  return (
    <Layout>
      {/* HERO — Go Ape style full-bleed video */}
      <section className="relative overflow-hidden bg-primary">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
          <img
            src={heroBg.url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
          />
          <iframe
            src="https://www.youtube-nocookie.com/embed/FKe_C2Sq5_I?autoplay=1&mute=1&controls=0&loop=1&playlist=FKe_C2Sq5_I&playsinline=1&rel=0&modestbranding=1&disablekb=1&iv_load_policy=3"
            title="Bosque Suspendido — vídeo de aventura"
            tabIndex={-1}
            allow="autoplay; encrypted-media"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-screen h-[56.25vw] min-h-full min-w-[177.78vh] border-0"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/15 to-primary/45" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 pt-44 pb-16 md:pt-64 md:pb-20 min-h-[82vh] flex flex-col justify-end items-start">
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-3 wood-btn font-semibold px-14 py-5 text-sm tracking-[0.3em] uppercase"
            >
              {tk("home.hero.cta.book", "Reserva ahora")}{" "}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/services/areas"
              className="inline-flex items-center gap-3 rounded-full border-2 border-primary-foreground/70 text-primary-foreground px-8 py-4 text-xs tracking-[0.3em] uppercase font-bold hover:border-gold-soft hover:text-gold-soft transition-colors"
            >
              {tk("home.hero.cta.activities", "Ver actividades")}
            </Link>
          </div>
          <p className="mt-7 text-[11px] tracking-[0.3em] uppercase font-bold text-gold-soft drop-shadow">
            {tk("home.hero.note", "La reserva previa es obligatoria y gratuita")}
          </p>
        </div>
      </section>

      {/* WHAT IS BS — asymmetric split: photo collage + condensed copy */}
      <section className="border-b border-gold/15 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          {/* Photo collage */}
          <div className="lg:col-span-7 grid grid-cols-2 gap-4 relative">
            <div className="space-y-4">
              <div className="rounded-3xl overflow-hidden shadow-lg border-4 border-card h-64 md:h-80">
                <img
                  src={introZiplineImg}
                  alt="Tirolina entre los pinos del Bosque Suspendido"
                  width={768}
                  height={960}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-lg border-4 border-card h-52 md:h-64">
                <img
                  src={introFamilyImg}
                  alt="Familia con arneses en un circuito de aventura en los árboles"
                  width={768}
                  height={768}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="flex items-center pt-8">
              <div className="rounded-3xl overflow-hidden shadow-lg border-4 border-card w-full h-[26rem] md:h-[34rem]">
                <img
                  src={introForestImg}
                  alt="Pinar centenario junto a Doñana"
                  width={768}
                  height={1344}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            {/* Desde 2004 badge */}
            <div className="absolute -bottom-4 -left-4 md:-left-6 bg-primary text-primary-foreground rounded-full w-28 h-28 md:w-32 md:h-32 flex flex-col items-center justify-center text-center shadow-2xl shadow-black/30 border-4 border-gold -rotate-12">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold leading-none">
                {tk("home.intro.badge", "Desde")}
              </span>
              <span className="font-display text-3xl md:text-4xl font-extrabold leading-none mt-1">2004</span>
            </div>
          </div>

          {/* Condensed copy */}
          <div className="lg:col-span-5 flex flex-col items-start">
            <span className="inline-block px-3 py-1 rounded-md bg-gold/10 text-gold text-xs tracking-[0.3em] uppercase font-bold mb-4">
              {tk("home.kicker", "Naturaleza y diversión para toda la familia")}
            </span>
            <h1 className="font-display text-5xl md:text-6xl xl:text-7xl uppercase leading-[0.95] text-primary">
              {tk("home.hero.title.a", "Bosque")}
              <br />
              <span className="text-gold">{tk("home.hero.title.b", "Suspendido")}</span>
            </h1>
            <div className="mt-6 space-y-6">
              <h2 className="text-xl md:text-2xl font-extrabold text-primary leading-tight">
                {tk("home.intro.lead", "El primer parque de aventura en los árboles de Andalucía, a solo 15 minutos de Sevilla.")}
              </h2>
              <p className="text-foreground/85 leading-relaxed">
                {tk(
                  "home.intro.body",
                  "Descubre un pinar centenario de gran valor ecológico junto a Doñana. Emoción pura con tirolinas, circuitos de altura, escalada, paintball y lucha de arqueros: aventura, naturaleza y seguridad para todas las edades desde 2004.",
                )}
              </p>
              <div className="pt-2">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-3 wood-btn font-semibold px-10 py-4 text-xs tracking-[0.3em] uppercase"
                >
                  {tk("home.intro.cta", "¡Reserva tu aventura!")}
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO FOR */}
      <section className="border-b border-gold/15 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("home.who.kicker", "¿A quién va dirigido?")}</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4">{tk("home.who.title", "Para todas las edades y planes")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { i: Users, t: tk("home.who.particulares.t", "Particulares"), d: tk("home.who.particulares.d", "Solo, en pareja, en familia o con amigos — ven a disfrutar con nosotros.") },
              { i: PartyPopper, t: tk("home.who.grupos.t", "Grupos"), d: tk("home.who.grupos.d", "El lugar ideal para despedidas, clubes deportivos, empresas y grupos de cualquier tipo.") },
              { i: Cake, t: tk("home.who.cumples.t", "Cumpleaños"), d: tk("home.who.cumples.d", "Celebra cumpleaños, comuniones y fechas especiales con paquetes pensados para ti.") },
              { i: GraduationCap, t: tk("home.who.escolares.t", "Escolares"), d: tk("home.who.escolares.d", "Programas para más de 500 escolares simultáneamente, para todas las edades y ciclos.") },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="rounded-2xl border border-gold/20 bg-background/40 p-7 hover:border-gold/60 transition-colors flex flex-col">
                <Icon className="text-gold" size={28} strokeWidth={1.4} />
                <h3 className="mt-5 font-display text-2xl">{t}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACTIVITIES */}
      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("home.activities.kicker", "Qué puedes hacer")}
            </span>
            <h2 className="font-display text-5xl md:text-6xl mt-4">
              {tk("home.activities.title", "Actividades")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((a) => (
              <Link
                key={a.slug}
                to="/services/areas"
                className="group rounded-2xl border border-gold/15 bg-card/40 overflow-hidden hover:border-gold/60 hover:bg-card/60 transition-colors flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden bg-card">
                  <img
                    src={a.imageUrl || FALLBACK_IMAGES[a.slug]}
                    alt={a.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-2xl group-hover:text-gold transition-colors">{a.name}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{a.desc}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold">
                    {tk("home.activities.cta", "Saber más")} <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/services/areas"
              className="text-xs tracking-[0.3em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold inline-flex items-center gap-2"
            >
              {tk("home.activities.all", "Todas las actividades")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* SAFETY */}
      <section className="relative border-b border-gold/15 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/10 via-background to-background" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("home.safety.kicker", "Seguridad y confianza")}</span>
            <h2 className="font-display text-4xl md:text-6xl mt-5 leading-[1.05]">
              {tk("home.safety.title.a", "Línea de vida ")}
              <span className="gold-gradient">{tk("home.safety.title.b", "continua")}</span>
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-xl">
              {tk("home.safety.text", "La seguridad es un factor fundamental en nuestras actividades. Contamos con sistemas de línea de vida continua que impiden la desconexión accidental del participante. Solo tienes que preocuparte de disfrutar.")}
            </p>
            <ul className="mt-8 space-y-4">
              {[
                tk("home.safety.b1", "Más de 400 árboles tratados y cuidados con endoterapia respetuosa."),
                tk("home.safety.b2", "Inspecciones periódicas por empresa externa especializada."),
                tk("home.safety.b3", "Personal cualificado con formación continua."),
                tk("home.safety.b4", "Empresa inscrita en el Registro Andaluz de Turismo Activo (SE-0013)."),
              ].map((b) => (
                <li key={b} className="flex gap-3 text-foreground/85">
                  <ShieldCheck className="text-gold shrink-0 mt-0.5" size={18} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { v: "2004", l: tk("home.safety.s1", "Abrimos nuestras puertas") },
              { v: "400+", l: tk("home.safety.s2", "Árboles cuidados") },
              { v: "500+", l: tk("home.safety.s3", "Escolares simultáneamente") },
              { v: "100%", l: tk("home.safety.s4", "Diversión y seguridad") },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl border border-gold/25 bg-card/40 p-6">
                <div className="font-display text-4xl gold-gradient">{s.v}</div>
                <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-gold/15 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
          {[
            { n: 20, suffix: "+", l: tk("home.stat.years", "Años de aventura") },
            { n: 9, suffix: "", l: tk("home.stat.activities", "Actividades distintas") },
            { n: 400, suffix: "+", l: tk("home.stat.trees", "Árboles en el parque") },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-6xl md:text-7xl gold-gradient">
                <CountUp end={s.n} suffix={s.suffix} />
              </div>
              <div className="mt-4 text-[11px] tracking-[0.3em] uppercase text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LOCATION */}
      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("home.loc.kicker", "Horario y cómo llegar")}</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4">{tk("home.loc.title", "En pleno pinar, junto a Sevilla")}</h2>
            <div className="gold-divider my-6 max-w-[100px]" />
            <ul className="space-y-4 text-foreground/85">
              <li className="flex gap-3"><MapPin className="text-gold shrink-0 mt-0.5" size={18} /><span>Centro de Naturaleza La Juliana<br/>Ctra. A-474 Bollullos de la Mitación — Aznalcázar, Km 15,5</span></li>
              <li className="flex gap-3"><Calendar className="text-gold shrink-0 mt-0.5" size={18} /><span>{tk("home.loc.hours", "Sábados, domingos y festivos de 10:30 a 15:00. Octubre, abril y mayo: 10:30 – 18:00. Julio y agosto solo grupos concertados.")}</span></li>
              <li className="flex gap-3"><Phone className="text-gold shrink-0 mt-0.5" size={18} /><a className="hover:text-gold" href="tel:+34955778035">+34 955 778 035</a></li>
              <li className="flex gap-3"><Mail className="text-gold shrink-0 mt-0.5" size={18} /><a className="hover:text-gold" href="mailto:info@bosquesuspendido.com">info@bosquesuspendido.com</a></li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://www.google.es/maps/place/Bosque+Suspendido+-+Parque+de+Aventuras+en+los+%C3%81rboles/@37.315081,-6.1706687,17z"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-7 py-3 text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors"
              >
                <MapPin size={14} /> {tk("home.loc.map", "Cómo llegar")}
              </a>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-gold/20 aspect-[4/3]">
            <iframe
              title="Mapa Bosque Suspendido"
              src="https://www.google.com/maps?q=Bosque+Suspendido+La+Juliana&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${heroBg.url})` }} aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/60" aria-hidden />
        <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("home.cta.kicker", "Te esperamos en el pinar")}
          </span>
          <h2 className="font-display text-5xl md:text-7xl mt-5">
            <TreePine className="inline mr-3 text-gold" size={48} strokeWidth={1.5} />
            {tk("home.cta.title", "¡Reserva tu aventura!")}
          </h2>
          <p className="mt-6 text-foreground/90 max-w-2xl mx-auto leading-relaxed">
            {tk("home.cta.text", "La reserva previa es obligatoria y gratuita. Escríbenos o llámanos y prepárate para disfrutar.")}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="wood-btn px-9 py-4 text-xs tracking-[0.3em] uppercase font-semibold"
            >
              {tk("home.cta.book", "Reservar")}
            </Link>
            <a
              href="mailto:info@bosquesuspendido.com"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-9 py-4 text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors"
            >
              <Mail size={14} /> info@bosquesuspendido.com
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
